import type { Event } from "../types/iosModels";
import { normalizeMediaUrl } from "./mediaUrls";

export type RaceCoverSlot = "mobile" | "web";
export const RACE_COVER_MAX_BYTES = 10 * 1024 * 1024;
export const RACE_COVER_MAX_PIXELS = 40_000_000;
export const RACE_COVER_SLOTS = {
  mobile: {
    label: "Mobile Cover Image",
    usage: "Used on iOS and Android.",
    width: 1200, height: 1500, ratio: "4 / 5",
    requirements: "Portrait · 4:5 · At least 1200 × 1500 px",
    invalid: "This image must be portrait (4:5) and at least 1200 × 1500 px.",
    required: "Upload a mobile cover image.",
  },
  web: {
    label: "Web Cover Image — Landscape",
    usage: "Used throughout the web experience, including mobile browsers.",
    width: 1920, height: 1080, ratio: "16 / 9",
    requirements: "Landscape · 16:9 · At least 1920 × 1080 px",
    invalid: "This image must be landscape (16:9) and at least 1920 × 1080 px.",
    required: "Upload a web cover image.",
  },
} as const;

export class RaceCoverValidationError extends Error {
  constructor(public slot: RaceCoverSlot, message: string) { super(message); }
}

export const getRaceCoverDimensionsError = (slot: RaceCoverSlot, width: number, height: number): string | null => {
  const spec = RACE_COVER_SLOTS[slot];
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) ||
      width < spec.width || height < spec.height || width * spec.height !== height * spec.width) return spec.invalid;
  if (width * height > RACE_COVER_MAX_PIXELS) return "Use an image with no more than 40 megapixels.";
  return null;
};

const dimensionsCache = new WeakMap<File, Promise<{ width: number; height: number }>>();
const readDimensions = (file: File): Promise<{ width: number; height: number }> => {
  const cached = dimensionsCache.get(file);
  if (cached) return cached;
  const promise = new Promise<{ width: number; height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const cleanup = () => { clearTimeout(timer); image.onload = null; image.onerror = null; URL.revokeObjectURL(url); };
    const fail = () => { cleanup(); reject(new Error("This image could not be read. Choose another JPEG or PNG file.")); };
    const timer = setTimeout(fail, 15000);
    image.onload = () => {
      const result = { width: image.naturalWidth, height: image.naturalHeight };
      cleanup();
      resolve(result);
    };
    image.onerror = fail;
    // Browsers apply EXIF orientation before reporting natural dimensions.
    image.src = url;
  });
  dimensionsCache.set(file, promise);
  promise.catch(() => dimensionsCache.delete(file));
  return promise;
};

const readBytes = (file: File): Promise<Uint8Array> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
  reader.onerror = () => reject(new Error("This image could not be read. Choose another JPEG or PNG file."));
  reader.readAsArrayBuffer(file);
});

export async function validateRaceCoverFile(file: File, slot: RaceCoverSlot): Promise<{ width: number; height: number }> {
  try {
    if (!["image/jpeg", "image/png"].includes(file.type)) throw new Error("Choose a JPEG or PNG image.");
    if (file.size > RACE_COVER_MAX_BYTES) throw new Error("This image exceeds 10 MB. Choose a smaller file.");
    const bytes = await readBytes(file);
    const png = [137, 80, 78, 71, 13, 10, 26, 10].every((byte, index) => bytes[index] === byte);
    const jpeg = bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
    if ((file.type === "image/png" && !png) || (file.type === "image/jpeg" && !jpeg)) {
      throw new Error("This image could not be read. Choose another JPEG or PNG file.");
    }
    if (png) {
      const view = new DataView(bytes.buffer);
      for (let offset = 8; offset + 12 <= bytes.length;) {
        if (String.fromCharCode(...Array.from(bytes.slice(offset + 4, offset + 8))) === "acTL") {
          throw new Error("Animated images are not supported. Choose a still JPEG or PNG image.");
        }
        offset += view.getUint32(offset) + 12;
      }
    }
    const dimensions = await readDimensions(file);
    const error = getRaceCoverDimensionsError(slot, dimensions.width, dimensions.height);
    if (error) throw new Error(error);
    return dimensions;
  } catch (error) {
    throw new RaceCoverValidationError(slot, error instanceof Error ? error.message : "This image could not be read. Choose another JPEG or PNG file.");
  }
}

// Only legacy events use their generic image/gallery. A missing new slot never
// substitutes the other platform's asset.
export function getWebRaceCover(event: Pick<Event, "webCoverImageUrl" | "mobileCoverImageUrl" | "photoFirebaseURL" | "photosFirebaseURLs">, fallback = "/assets/landing/hero-rider.jpg"): string {
  return normalizeMediaUrl(event.webCoverImageUrl) ||
    (!event.mobileCoverImageUrl ? normalizeMediaUrl(event.photoFirebaseURL) ||
      event.photosFirebaseURLs?.map(normalizeMediaUrl).find(Boolean) : null) ||
    fallback;
}
